/**
 * SIL-1346 - the ONE buyer-verification status hook for the customer portal.
 * Spec: docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md §5.
 *
 * `GET /api/client/buyer-verification` (shipped in slice 1, SIL-1322) is read
 * here and nowhere else. The verify banner, the status pill, the listing
 * action card and every action gate (`useVerificationGate`) call this hook
 * instead of fetching the endpoint themselves - one cache entry, so those
 * surfaces can never disagree with each other about whether the org is
 * verified, and an invalidation after submit refreshes all of them at once.
 *
 * Acceptance criterion (SIL-1346): a grep for `buyer-verification` under
 * `apps/customer/src` finds exactly one `api.api.client[...]` call site, this
 * file's. Adding a second one is the bug this hook exists to prevent.
 */
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { BuyerVerificationResponse, BuyerVerificationStatus } from '@siliconx/shared';
import { api, unwrap } from '../lib/api';
import { qk } from '../lib/queryKeys';

/**
 * Status is a server-side decision that changes only on an ops review, so a
 * short staleTime keeps several mounted consumers (banner + pill + action
 * card on the same page) down to a single round trip without ever showing a
 * status the server has already moved on from for long.
 */
const BUYER_STATUS_STALE_MS = 30_000;

/**
 * The hc sub-client for `/api/client/buyer-verification`, and the ONE place in
 * the app that names that path.
 *
 * SIL-1350 needs the endpoint's WRITE routes (`PUT /draft`, `/document/*`,
 * `/sms/*`, `/submit`) from the verification modal. Spelling
 * `api.api.client['buyer-verification']` a second time there would satisfy no
 * one: it trips this module's own single-call-site test, and more importantly
 * it re-creates exactly the thing that test exists to prevent - a second file
 * that knows the endpoint path and can drift from this one. Exporting the
 * sub-client instead keeps the path written once and still gives the modal the
 * full typed surface. See components/exchange/buyerVerificationApi.ts.
 */
export const buyerVerificationEndpoint = api.api.client['buyer-verification'];

export function useBuyerStatus(): UseQueryResult<BuyerVerificationResponse> {
  return useQuery({
    queryKey: qk.buyerVerification(),
    queryFn: async () => unwrap(await buyerVerificationEndpoint.$get()),
    staleTime: BUYER_STATUS_STALE_MS,
  });
}

/**
 * Invalidate the single buyer-status cache entry - call after `POST /submit`
 * (spec §4.5) or after any action that could have changed the org's status.
 * Every consumer of `useBuyerStatus` refetches; nothing else needs touching.
 */
export function useInvalidateBuyerStatus(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: qk.buyerVerification() });
}

/**
 * SIL-1559: write a `PUT /draft` response straight into the status cache. The
 * modal seeds its form from this cache on open, so without it a draft saved
 * seconds ago would reopen from the stale pre-save snapshot (staleTime is 30s)
 * and the banner would keep saying "Get verified" for a draft that exists.
 */
export function useSetBuyerStatus(): (response: BuyerVerificationResponse) => void {
  const queryClient = useQueryClient();
  return (response) => {
    queryClient.setQueryData(qk.buyerVerification(), response);
  };
}

/**
 * Invalidate AND read back the resulting status, for the one caller that
 * cannot use the status a render already closed over: `useVerificationGate`
 * handling a `403 BUYER_VERIFICATION_REQUIRED`. A 403 on an action the client
 * believed was allowed means the cached status is wrong, so the gate must
 * decide "modal or toast" from the refreshed value, not the stale one - and a
 * hook's return value only updates on the NEXT render, which is too late for
 * a decision taken inside the catch block.
 *
 * Falls back to whatever is still cached when the refetch itself fails: a
 * network blip must not turn into a silently skipped gate.
 */
export function useRefreshBuyerStatus(): () => Promise<BuyerVerificationStatus | undefined> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: qk.buyerVerification() });
    return queryClient.getQueryData<BuyerVerificationResponse>(qk.buyerVerification())?.status;
  };
}

/**
 * True only for the terminal `verified` status AND a current Exchange User
 * Agreement acceptance (SIL-1359). Deliberately a function over the status
 * rather than a boolean field on the response: `undefined` (status not
 * loaded yet) must read as "not verified" at every call site, and an inline
 * `status === 'verified'` in each component is how one of them ends up
 * writing `status !== 'unverified'` instead.
 *
 * `needsAgreementReacceptance` is optional so every pre-SIL-1359 call site
 * that only had a status in hand keeps compiling and keeps its prior
 * behavior — omitting it is never itself a false "verified".
 */
export function isBuyerVerified(
  status: BuyerVerificationStatus | undefined,
  needsAgreementReacceptance?: boolean,
): boolean {
  return status === 'verified' && !needsAgreementReacceptance;
}
